import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  CalendarDays,
  Check,
  ArrowRight,
  Phone,
  MessageCircle,
  CalendarClock,
} from "lucide-react";
import { CATEGORY_LABELS, COURSE_SKRIPTE } from "@/data/courses";
import { BookOpen, Mail } from "lucide-react";
import { formatNextStart } from "@/lib/nextStart";
import { TrustInline } from "@/components/site/TrustInline";
import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parsePrice = (price) => {
  const s = String(price).replace(/[^\d.,]/g, "").replace(/\./g, "").replace(",", ".");
  return Number(s) || 0;
};
const fmtEur = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CourseDetailDialog({ course, open, onOpenChange, onBook, onInfopaket }) {
  const [flyerBusy, setFlyerBusy] = useState(false);
  if (!course) return null;

  const handleBuy = () => onBook(course);
  const skript = COURSE_SKRIPTE[course.id];
  const basePrice = parsePrice(course.price);
  const monthly12 = basePrice > 0 ? Math.floor((basePrice * 100) / 12) / 100 : 0;

  const downloadFlyer = async () => {
    setFlyerBusy(true);
    try {
      const r = await axios.post(`${API}/courses/flyer-pdf`, course, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Flyer-${course.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Flyer download failed", err);
    } finally {
      setFlyerBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="course-detail-dialog"
        className="max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden bg-cream-50 border-0 p-0 rounded-3xl sm:rounded-3xl"
      >
        {/* ── Hero · warme Blush-Szene ── */}
        <div className="relative text-sand-800 p-7 md:p-10 overflow-hidden" style={{ backgroundColor: "#F6E8DC" }}>
          <div className="relative">
            <div className="flex items-center gap-2.5 mb-6 flex-wrap">
              <span className="font-poster text-[10.5px] tracking-[0.24em] uppercase font-medium text-terra">
                {CATEGORY_LABELS[course.category]}
              </span>
              <span className="h-px w-8 bg-terra/50" />
              <span className="inline-flex items-center gap-1.5 bg-terra text-cream-50 text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full">
                <CalendarDays className="h-3 w-3" strokeWidth={2.2} />
                Start: {formatNextStart(course.nextStart)}
              </span>
              {basePrice > 0 && (
                <span className="inline-flex items-center gap-1.5 border border-terra/50 text-terra text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full">
                  <CalendarClock className="h-3 w-3" strokeWidth={2.2} />
                  Ratenzahlung möglich
                </span>
              )}
            </div>
            <DialogHeader className="text-left">
              <DialogTitle className="font-magazine font-medium text-[2.2rem] md:text-[3rem] leading-[1.08] text-sand-800">
                {course.title}
              </DialogTitle>
              <DialogDescription className="text-sand-600 text-[15px] mt-3 italic">
                {course.subtitle}
              </DialogDescription>
            </DialogHeader>

            <p className="mt-5 text-sand-700 text-[14.5px] leading-[1.7] max-w-2xl">
              {course.description}
            </p>

            {/* Facts als Editorial-Strip */}
            {(() => {
              const audienceLong = (course.audience || "").length > 70;
              return (
                <>
                  <div className={`mt-8 grid grid-cols-2 ${audienceLong ? "md:grid-cols-3" : "md:grid-cols-4"} gap-x-6 gap-y-4 border-t border-sand-800/15 pt-6`}>
                    <Fact label="Umfang" value={course.duration} />
                    <Fact label="Dauer" value={course.weeks} />
                    <Fact label="Format" value={course.format || "100% Live-Online"} />
                    {!audienceLong && <Fact label="Zielgruppe" value={course.audience} />}
                  </div>
                  {audienceLong && (
                    <div className="mt-6" data-testid="dialog-audience-block">
                      <div className="text-[9.5px] uppercase tracking-[0.26em] text-terra font-bold">Zielgruppe</div>
                      <p className="text-[13px] text-sand-700 leading-[1.65] mt-1.5 max-w-2xl">{course.audience}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="p-7 md:p-10 space-y-10">
          {/* Schedule */}
          <Section n="01" title="Kurszeiten">
            {Array.isArray(course.schedule) ? (
              <div className="grid sm:grid-cols-3 gap-3">
                {course.schedule.map((s) => (
                  <div key={s} className="rounded-xl bg-white/70 border border-sand-800/10 p-3.5 font-mono text-[12px] tracking-[0.04em] text-sand-700 leading-relaxed">
                    {s}
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-mono text-[12.5px] text-sand-700">{course.schedule}</p>
            )}
          </Section>

          {/* Curriculum */}
          {course.curriculum?.length > 0 && (
            <Section n="02" title="Das lernst du">
              <ul className="space-y-3">
                {course.curriculum.map((c, i) => (
                  <li key={i} className="flex gap-4 items-baseline text-sand-700 text-[14.5px] leading-relaxed">
                    <span className="font-mono text-[11px] tracking-[0.1em] text-terra shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Outcomes */}
          {course.outcomes?.length > 0 && (
            <Section n="03" title="Das nimmst du mit">
              <ul className="space-y-3">
                {course.outcomes.map((o, i) => (
                  <li key={i} className="flex gap-3 items-start text-sand-800 text-[14.5px] font-medium leading-relaxed">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-saffron text-espresso shrink-0">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {o}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* ── Skript inklusive · Desenio-Koralle ── */}
          {skript && (
            <div data-testid="dialog-skript-block" className="bg-coral text-cream-100 p-6 md:p-7 rounded-2xl">
              <div className="flex items-center gap-5 md:gap-7">
                <img
                  src={skript.cover}
                  alt={skript.title}
                  data-testid="dialog-skript-cover"
                  className="w-24 sm:w-28 rounded-md shadow-xl rotate-[-3deg] ring-1 ring-white/25 shrink-0"
                  loading="lazy"
                />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.26em] font-semibold text-cream-100/85">
                    Inklusive · Ohne Aufpreis
                  </div>
                  <div className="font-magazine text-[1.35rem] md:text-[1.6rem] leading-[1.15] mt-1.5">
                    Das offizielle Kursskript – <span className="italic underline decoration-cream-100/60 decoration-2 underline-offset-4">geschenkt</span>.
                  </div>
                  <p className="mt-2 text-[13px] text-cream-100/90 leading-snug">
                    „{skript.title}“ als PDF – digital + druckbar. Im Shop nur im Lernpaket ({skript.paketWert} €) erhältlich, für dich als Teilnehmer:in kostenlos.
                  </p>
                  <span data-testid="dialog-skript-pages" className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream-100 text-coral-deep text-[12px] font-bold px-3.5 py-1.5">
                    <BookOpen className="h-3.5 w-3.5" strokeWidth={2.2} />
                    {skript.volumes || `${skript.pages.toLocaleString("de-DE")} Seiten`}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Preis + Ratenzahlung + CTA · heller Karten-Block ── */}
          <div className="bg-white/80 border border-sand-800/10 rounded-2xl text-sand-800 p-6 md:p-8" data-testid="dialog-price-block">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-sand-500 font-bold">Preis</div>
                <div className="font-magazine text-[2.8rem] leading-none mt-2 text-terra">{course.price}</div>
                {course.priceNote && (
                  <div className="mt-2 text-[12px] text-sand-600 font-medium" data-testid="dialog-price-note">{course.priceNote}</div>
                )}
                <div className="mt-3 font-mono text-[11px] tracking-[0.08em] text-sand-500 uppercase">{course.funding}</div>
                <div className="mt-4">
                  <TrustInline compact testId="dialog-trust" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                <Button
                  onClick={handleBuy}
                  data-testid="dialog-buy-cta"
                  className="rounded-full bg-terra hover:bg-terra-dark text-cream-50 h-13 px-8 py-3.5 text-[12.5px] font-bold uppercase tracking-[0.12em] group transition-colors"
                >
                  Jetzt buchen
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
                </Button>
                <Button asChild variant="outline" className="rounded-full h-13 px-8 py-3.5 text-[12.5px] font-bold uppercase tracking-[0.12em] border-terra/50 text-terra hover:bg-terra/10 hover:text-terra-dark bg-transparent">
                  <a href="#kontakt" onClick={() => onOpenChange(false)}>
                    Beratung anfragen
                  </a>
                </Button>
              </div>
            </div>
            {basePrice > 0 && (
              <div className="mt-6 flex items-center gap-3 rounded-xl bg-saffron text-espresso px-4 py-3" data-testid="dialog-raten-hint">
                <CalendarClock className="h-4.5 w-4.5 shrink-0" strokeWidth={2.2} />
                <span className="text-[13px] font-bold leading-snug">
                  Ratenzahlung ohne Aufschlag: ab {fmtEur(monthly12)} €/Monat — wahlweise 3, 6 oder 12 Monatsraten, endet automatisch.
                </span>
              </div>
            )}
          </div>

          {/* Flyer + Help */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 border-t border-sand-800/15 pt-6">
            <button
              onClick={() => {
                if (onInfopaket) { onOpenChange(false); onInfopaket(course); return; }
                downloadFlyer();
              }}
              disabled={flyerBusy}
              data-testid="dialog-flyer-btn"
              className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-sand-800 border-b-2 border-terra pb-1 hover:text-terra transition-colors disabled:opacity-50"
            >
              <Mail className="h-4 w-4" strokeWidth={2} />
              {onInfopaket ? "Infopaket per E-Mail" : (flyerBusy ? "Flyer wird erzeugt …" : "Flyer als PDF")}
            </button>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-sand-600">
              <a href="tel:+4921129273330" className="inline-flex items-center gap-2 hover:text-terra transition-colors">
                <Phone className="h-3.5 w-3.5" strokeWidth={1.8} /> 0211 29273330
              </a>
              <a href="https://wa.me/491622572545" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-terra transition-colors">
                <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.8} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-[0.26em] text-terra font-bold">{label}</div>
      <div className="text-[13px] text-sand-800 font-medium leading-snug mt-1.5" title={value}>
        {value}
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="font-mono text-[11px] tracking-[0.2em] text-terra">{n}</span>
        <h3 className="font-magazine font-medium text-[1.5rem] md:text-[1.7rem] text-sand-800 leading-none">
          {title}
        </h3>
        <span className="h-px flex-1 bg-terra/25" />
      </div>
      {children}
    </div>
  );
}
